import { FormSkeleton } from "../../components/Loading";
import { track, reportError } from "../../lib/telemetry";
import "./preferences.css";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { accountKey, loadAccount, savePreferences } from "../../lib/account";
import { isNative, requestNotifications } from "../../lib/platform";
import {
  savePushToken,
  notificationPermissionHint,
} from "../../lib/notifications";
import { browserPushStatus } from "../../lib/browser-push";
const categories = {
  team_posts: "Team posts",
  following_posts: "Posts from people you follow",
  post_replies: "Replies to your posts",
  mentions: "Mentions",
  reactions: "Reactions",
  post_reminders: "Weekly reminders",
};
const details: Record<string, string> = {
  team_posts: "When a teammate posts",
  following_posts: "When someone you follow posts",
  post_replies: "Comments on your posts",
  mentions: "When someone @mentions you",
  reactions: "When someone reacts to your post",
  post_reminders: "Thu, Sat and Sun night if you’re behind",
};
export function Preferences({ onComplete }: { onComplete: () => void }) {
  const query = useQuery({ queryKey: accountKey, queryFn: loadAccount });
  const cache = useQueryClient();
  const [changes, setChanges] = useState<Record<string, boolean>>({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const existing = query.data?.profile?.notification_preferences || {};
  async function save() {
    if (busy) return;
    track("notification_preferences_save_attempted", { changed_count: Object.keys(changes).length });
    setBusy(true);
    setError("");
    // Start the permission request inside this user gesture, before Supabase I/O.
    const permissionRequest = requestNotifications().then(
      (result) => ({ result, error: null }),
      (error) => ({ result: null, error }),
    );
    let persisted = false;
    let followUp = "notification_preferences_refresh";
    try {
      const defaults = Object.fromEntries(
        Object.keys(categories).map((k) => [k, existing[k] ?? true]),
      );
      const permission = await permissionRequest;
      if (permission.error)
        throw new Error(
          "Notification setup failed. Please try Save & continue again. " +
            (permission.error instanceof Error ? permission.error.message : ""),
        );
      track("notification_permission_completed", { token_available: !!permission.result?.token });
      if (permission.result?.token)
        await savePushToken(permission.result.token);
      await savePreferences({
        ...existing,
        ...defaults,
        ...changes,
        reviewed_season_id: query.data?.application?.season_id,
      });
      persisted = true;
      track("notification_preferences_save_succeeded");
      await cache.invalidateQueries({ queryKey: accountKey });
      followUp = "notification_preferences_continue";
      await onComplete();
    } catch (e) {
      if (!persisted) track("notification_preferences_save_failed");
      reportError(e, persisted ? followUp : "notification_preferences_save");
      setError(e instanceof Error ? e.message : "Could not save preferences");
    } finally {
      setBusy(false);
    }
  }
  if (query.isPending) return <FormSkeleton />;
  if (query.error) return <p role="alert">{query.error.message}</p>;
  return (
    <main className="page notification-preferences">
      <h1>Your notifications</h1>
      <p className="subtitle">Choose what you’d like to hear about. You can change these any time in Settings.</p>
      {!isNative() && notificationPermissionHint(browserPushStatus()) && (
        <p className="preferences-note">
          {notificationPermissionHint(browserPushStatus())}
        </p>
      )}
      <div className="preferences-list">
      {Object.entries(categories).map(([key, label]) => (
        <label className="toggle" key={key}>
          <span><b>{label}</b><small>{details[key]}</small></span>
          <input
            type="checkbox"
            role="switch"
            checked={changes[key] ?? existing[key] ?? true}
            onChange={(e) => {
              track("notification_preference_changed", { category: key, enabled: e.target.checked });
              setChanges({ ...changes, [key]: e.target.checked });
            }}
          />
        </label>
      ))}
      </div>
      {error && <p role="alert">{error}</p>}
      <footer className="actions">
        <button disabled={busy} onClick={save}>
          {busy ? "Saving…" : "Save & continue"}
        </button>
        {isNative() && <p className="preferences-caption">iOS asks to allow notifications next.</p>}
      </footer>
    </main>
  );
}
